import { useState, useEffect } from 'react';
import { DashboardData, KpiData, ShortKpiData } from './types';
import { fetchData } from '../../axios/fetchData';
import { useNavigate } from 'react-router';
import { severity, useNotification } from '../../components';
import { DateRangeState } from '../../components/Dashboard/DateRangeControl/useDateRange';
import { ENDPOINT_MAP } from './constants';
import { isDevMode } from '../../utils/isDevMode';
import { getDateRange } from '../../components/Dashboard/DateRangeControl/helpers';

type DashboardConfigType = {
  dateRange: DateRangeState;
  salonId: string;
};

export const useDashboardData = ({ salonId, dateRange }: DashboardConfigType) => {
  const { showMessage } = useNotification();
  const navigate = useNavigate();

  const defaultIsLoading: Record<string, boolean> = {};
  Object.keys(ENDPOINT_MAP).forEach((key) => {
    defaultIsLoading[key] = false;
  });
  const [isLoading, setIsLoading] = useState<Record<string, boolean>>(defaultIsLoading);

  const [data, setData] = useState<DashboardData>({});

  useEffect(() => {
    async function load() {
      const { dateString } = getDateRange({
        period: dateRange.name,
        year: dateRange.year,
        quarter: dateRange.quarter,
      });

      (Object.keys(ENDPOINT_MAP) as Array<keyof DashboardData>).forEach(async (key) => {
        setIsLoading((prev) => {
          return { ...prev, [key]: true };
        });
        const endpoint = ENDPOINT_MAP[key];

        const url = isDevMode()
          ? `${import.meta.env.VITE_APP_SALON_DASHBOARD}${endpoint}.json`
          : `${import.meta.env.VITE_APP_SALON_DASHBOARD}${salonId}/${endpoint}/cd/${dateString}`;

        try {
          const result = await fetchData(
            {
              url,
              params: {
                _format: 'json',
              },
            },
            (message: string, type: severity) => {
              if (message !== 'A Dashboard data not found.') showMessage(message, type);
            },
            navigate,
          );

          if (result) {
            setData((prev) => ({
              ...prev,
              [key]: result.data as KpiData | ShortKpiData,
            }));
          }
        } catch (err) {
          console.error(`Error loading ${endpoint}`, err);
        }
        setIsLoading((prev) => {
          return { ...prev, [key]: false };
        });
      });
    }

    setData({});

    if (salonId && dateRange) {
      load();
    }
  }, [salonId, dateRange]);

  return {
    data,
    isLoading,
  };
};
